import { Icon } from "../../../components/icons";
import { WHATSAPP_AUTO_LIVE } from "../content";
import { MOCK_CLUB, MOCK_ELIM_PLAYERS as P, MOCK_GROUP_A, MOCK_LADDER } from "../mockData";
import { publicAsset } from "../publicAsset";
import { MockFigure, NotificationCard, PanelFrame } from "./frames";
import "./CourtCalendarMock.css";

/**
 * M6 · Calendario de canchas (escena D2 de /organizador). Copia la cuadrícula
 * de AdminTournamentSchedulePage (leyenda, celdas libres/ocupadas/bloqueadas,
 * seleccionado y motivos del cambio) con los textos reales de la app:
 * - aviso verde: `flashOk("Orden automático aplicado: …")` (la app aún dice
 *   «slots»; aquí va «horarios», el cambio pendiente de la PR 5);
 * - aviso rojo: ConflictError de `matches/services.py` («Choque de jugador…»);
 * - notificación: título «Rol de juego» y cuerpo de `notify_match_schedule`.
 * A ≤560 px se ocultan las canchas 3 y 4 y aparece «+2 canchas».
 */

const surname = (fullName: string): string => fullName.trim().split(/\s+/).pop() ?? fullName;

// Jugadoras del Grupo A (Femenil 4.0) y de la escalera, del universo inventado.
const ORTIZ = MOCK_GROUP_A[0].name; // Mariana Ortiz
const MENDEZ = MOCK_GROUP_A[1].name; // Paola Méndez
const CARDENAS = MOCK_GROUP_A[3].name; // Sofía Cárdenas
const FUENTES = MOCK_LADDER[0].name; // Regina Fuentes
const ROBLES = MOCK_LADDER[1].name; // Camila Robles

type Slot =
  | { kind: "match"; label: string; a: string; b: string; selected?: boolean }
  | { kind: "conflict"; label: string; a: string; b: string }
  | { kind: "blocked"; reason: string }
  | { kind: "free"; target?: boolean };

const COURTS = ["Cancha 1", "Cancha 2", "Cancha 3", "Cancha 4"];

const match = (label: string, a: string, b: string, selected = false): Slot => ({
  kind: "match",
  label,
  a: surname(a),
  b: surname(b),
  selected,
});
// Pareja de dobles: «Fuentes/Aguilar»; puede cortar después de la diagonal (espacio de ancho cero).
const pair = (first: string, second: string) => `${surname(first)}/\u200b${surname(second)}`;
const FREE: Slot = { kind: "free" };

const ROWS: { time: string; slots: Slot[] }[] = [
  {
    time: "09:00",
    slots: [
      match("Cuartos #1", P.salazar, P.aguilar),
      match("Cuartos #2", P.herrera, P.torres),
      match("Cuartos #3", P.navarro, P.pena),
      match("Cuartos #4", P.castro, P.ruiz),
    ],
  },
  {
    time: "10:30",
    slots: [
      match("Grupo A #3", ORTIZ, CARDENAS),
      { kind: "conflict", label: "Grupo A #5", a: surname(ORTIZ), b: surname(MENDEZ) },
      FREE,
      { kind: "blocked", reason: "Mantenimiento" },
    ],
  },
  {
    time: "12:00",
    slots: [
      match("Semifinal #1", P.salazar, P.herrera, true),
      match("Semifinal #2", P.navarro, P.ruiz),
      { kind: "match", label: "Mixto", a: pair(FUENTES, P.aguilar), b: pair(ROBLES, P.torres) },
      { kind: "blocked", reason: "Comida" },
    ],
  },
  {
    time: "13:30",
    slots: [FREE, { kind: "free", target: true }, FREE, FREE],
  },
];

/** Motivos frecuentes de la app (SCHEDULE_CHANGE_REASONS). */
const REASONS = ["Lluvia", "Retraso", "Cancha no disponible", "A petición de jugadores"];
const ACTIVE_REASON = "Lluvia";

const AUTO_ORDER_NOTICE = "Orden automático aplicado: 24 partidos programados · 2 horarios ocupados evitados.";
const CONFLICT_NOTICE = `Choque de jugador: ${ORTIZ} ya tiene Grupo A #3 en ese mismo horario: 2026-10-17 a las 10:30. Cambia la hora o la fecha.`;
const CHANGE_NOTICE = `Cambio en tu partido (${ACTIVE_REASON}): vs ${P.herrera} — 17 oct, 13:30, Cancha 2`;

const FIGURE_LABEL =
  "Calendario de canchas del sábado 17 de octubre en cuatro canchas: partidos acomodados por horario, " +
  "dos horarios bloqueados por mantenimiento y comida, un choque de jugador detectado y un partido movido por lluvia " +
  `con el aviso que reciben los jugadores en su celular${WHATSAPP_AUTO_LIVE ? " y por WhatsApp" : ""}.`;

function colClass(index: number): string {
  return ` mk-cal__col--${index + 1}`;
}

function TapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V10" />
      <path d="M12 9.5a1.5 1.5 0 0 1 3 0v1" />
      <path d="M15 10.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-.6a5 5 0 0 1-3.9-1.9L5 15.4a1.5 1.5 0 0 1 2.3-1.9L9 15" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 11.6a8.4 8.4 0 0 1-12.3 7.5L3.5 20.5l1.4-4.5a8.4 8.4 0 1 1 15.6-4.4Z" />
      <path d="M9 10.2c.3 1.6 1.9 3.3 3.6 3.8l1.1-1 1.6.8" />
    </svg>
  );
}

/** «Salazar vs Aguilar»; en celdas angostas pasa a dos renglones y corta
 * antes del «vs» (nunca entre «vs» y el rival). */
function Names({ a, b }: { a: string; b: string }) {
  return (
    <span className="mk-cal__names">
      {a}{" "}
      <span className="mk-cal__rival">
        <i>vs</i>
        {"\u00a0"}
        {b}
      </span>
    </span>
  );
}

function SlotCell({ slot, index }: { slot: Slot; index: number }) {
  const col = colClass(index);
  switch (slot.kind) {
    case "match":
      return (
        <div className={`mk-cal__cell mk-cal__cell--busy${slot.selected ? " is-selected" : ""}${col}`}>
          <span className="mk-cal__label">{slot.label}</span>
          <Names a={slot.a} b={slot.b} />
        </div>
      );
    case "conflict":
      return (
        <div className={`mk-cal__cell mk-cal__cell--conflict${col}`}>
          <span className="mk-cal__x">×</span>
          <span className="mk-cal__label">{slot.label}</span>
          <Names a={slot.a} b={slot.b} />
        </div>
      );
    case "blocked":
      return (
        <div className={`mk-cal__cell mk-cal__cell--blocked${col}`}>
          <span className="mk-cal__label">
            <Icon name="lock" /> Bloqueado
          </span>
          <span className="mk-cal__reason">{slot.reason}</span>
        </div>
      );
    default:
      return slot.target ? (
        <div className={`mk-cal__cell mk-cal__cell--target${col}`}>
          <TapIcon />
          <span>Mover aquí</span>
        </div>
      ) : (
        <div className={`mk-cal__cell mk-cal__cell--free${col}`}>Libre</div>
      );
  }
}

export function CourtCalendarMock() {
  return (
    <MockFigure label={FIGURE_LABEL} className="mk-cal-figure">
      <div className="mk-cal">
        <PanelFrame title={`Calendario · sáb 17 oct 2026 · ${MOCK_CLUB.name}`} className="mk-cal__panel">
          <div className="mk-cal__legend">
            <span>
              <i className="mk-cal__swatch mk-cal__swatch--free" />
              Libre
            </span>
            <span>
              <i className="mk-cal__swatch mk-cal__swatch--busy" />
              Ocupado
            </span>
            <span>
              <i className="mk-cal__swatch mk-cal__swatch--blocked" />
              Bloqueado
            </span>
            <span>
              <i className="mk-cal__swatch mk-cal__swatch--selected" />
              Seleccionado
            </span>
          </div>

          <div className="mk-cal__flash mk-cal__flash--ok">
            <span className="mk-cal__flash-icon">
              <Icon name="check" />
            </span>
            <span>{AUTO_ORDER_NOTICE}</span>
          </div>

          <div className="mk-cal__grid">
            <span className="mk-cal__head mk-cal__head--time">Hora</span>
            {COURTS.map((court, index) => (
              <span key={court} className={`mk-cal__head${colClass(index)}`}>
                {court}
                {index === 1 ? <em className="mk-cal__more">+2 canchas</em> : null}
              </span>
            ))}
            {ROWS.map((row) => [
              <span key={row.time} className="mk-cal__time">
                {row.time}
              </span>,
              ...row.slots.map((slot, index) => <SlotCell key={`${row.time}-${index}`} slot={slot} index={index} />),
            ])}
          </div>

          <div className="mk-cal__flash mk-cal__flash--bad">
            <span className="mk-cal__flash-icon">×</span>
            <span>{CONFLICT_NOTICE}</span>
          </div>

          <div className="mk-cal__move">
            <div className="mk-cal__selected">
              <span className="mk-cal__eyebrow">Seleccionado</span>
              <strong>
                Semifinal #1 · {surname(P.salazar)} vs {surname(P.herrera)}
              </strong>
            </div>
            <div className="mk-cal__reasons">
              <span className="mk-cal__reasons-label">Motivo del cambio</span>
              {REASONS.map((reason) => (
                <span key={reason} className={`mk-cal__chip${reason === ACTIVE_REASON ? " is-active" : ""}`}>
                  {reason}
                </span>
              ))}
            </div>
          </div>
        </PanelFrame>

        <div className="mk-cal__notice">
          <NotificationCard
            className="mk-cal__notif"
            iconSrc={publicAsset("torneos123/icon-torneos123-192.png")}
            when="ahora"
            title="Rol de juego"
            body={CHANGE_NOTICE}
          />
          {WHATSAPP_AUTO_LIVE ? (
            <span className="mk-cal__channel">
              <span className="mk-cal__channel-icon">
                <ChatIcon />
              </span>
              También llega por WhatsApp
            </span>
          ) : null}
        </div>
      </div>
    </MockFigure>
  );
}
